/* eslint-disable react/jsx-boolean-value */
/* eslint-disable no-nested-ternary */
import React, { useState, useRef, useEffect } from 'react';
import { useDroppable, DragOverlay } from '@dnd-kit/core';
import {
  arrayOf, string, number, func, bool, shape
} from 'prop-types';
import { DraggableCabinet } from './dnd/DraggableCabinet';
import { DragOverlayCabinet } from './dnd/DragOverlayCabinet';
import { Cabinet } from './Cabinet';
import { inchesToPxs } from '../../../../utils/wall-size-utils';
import { CabinetShape, SegmentShape } from './type';
import { TouchPoint } from './TouchPoint';
import { useTouchPointManager } from '../hooks/useTouchPointManager';
import { useCabinetVisualizerStore } from '../../../../store/index';
import { useCabinetStore } from '../../../../store/ui';
import { cabinetToBoundingBox, getTotalBounding, getConstrainedWallHeight } from '../utils';

import {
  selectDesignAnchorSide,
  selectTopAndBottomProducts,
  DEFAULT_VIEWPORT_WALLSIZE,
  DEFAULT_TOUCH_POINT_WIDTH,
  DEFAULT_TOUCH_POINT_START_COLUMN,
  getSegments
} from '../../../../store/cabinetVisualizer/selectors';
import { Segment } from './Segment';
import { DimensionArrowsContainer } from './DimensionArrowsContainer';
import { CabinetDimensions } from './CabinetDimensions';
import { useIsMobileDevice } from '../../../../hooks/useIsMobileDevice';
import { DIRECTION } from '../../cabinetConstants';
import { tryGetQueryParam } from '../../../../utils/queryParams';

function originToScreen(origin, pxInInches) {
  const { Row, Column, Height, Width } = origin;

  return {
    Row: inchesToPxs(Row, pxInInches),
    Column: inchesToPxs(Column, pxInInches),
    Width: inchesToPxs(Width, pxInInches),
    Height: inchesToPxs(Height, pxInInches)
  };
}

function boundingToScreen(box, pxInInches) {
  const { top, left, height, width } = box;

  const result = {
    top: inchesToPxs(top, pxInInches),
    left: inchesToPxs(left, pxInInches),
    height: inchesToPxs(height, pxInInches),
    width: inchesToPxs(width, pxInInches)
  };

  if (box.right) {
    result.right = inchesToPxs(box.right, pxInInches);
  }

  if (box.bottom) {
    result.bottom = inchesToPxs(box.bottom, pxInInches);
  }

  return result;
}

function touchPointToScreen(touchPoint, pxInInches) {
  const result = boundingToScreen(touchPoint, pxInInches);
  result.type = touchPoint.type;

  return result;
}

export const Wall = React.memo(({
  rows,
  columns,
  segments,
  cabinets,
  pxInInches,
  zoom,
  onTouchPoint,
  onTouchPointForSpacer,
  onTouchPointForFiller,
  handleQuickView,
  handleDuplicate,
  designId,
  disableTouchPoints,
  showDimensions,
  scrollOffset,
  onFloorRectChange,
  onWallContainerRefChange,
  overlayPosition,
}) => {

  const dndEnabled = !(tryGetQueryParam('dndEnabled') === 'false');
  const enableTouchPointsOnBothSides = true; // tryGetQueryParam('enableTouchPointsOnBothSides') === 'true';
  const isMobile = useIsMobileDevice();
  const selectedCabinet = useCabinetStore((state) => state.selectedCabinet);
  const activeCabinet = useCabinetStore((state) => state.activeCabinet);
  const floorRef = useRef(null);
  const { setNodeRef } = useDroppable({
    id: 'wall-droppable', // Unique ID for the droppable area
  });

  const wallContainerRef = useRef(null);
  const combinedRef = (node) => {
    setNodeRef(node); // DnD Kit ref
    wallContainerRef.current = node;
    if (onWallContainerRefChange) {
      onWallContainerRefChange(node);
    }
  };
  const wallWidth = !columns ? DEFAULT_VIEWPORT_WALLSIZE.Columns : columns;
  const wallHeight = !rows || rows > DEFAULT_VIEWPORT_WALLSIZE.Rows
    ? DEFAULT_VIEWPORT_WALLSIZE.Rows : rows;

  const hasWallMeasurements = columns && columns > 0 && rows && rows > 0;
  const zoomedPxInInches = zoom * pxInInches;
  const convertedWidth = inchesToPxs(wallWidth, zoomedPxInInches);
  const convertedHeight = inchesToPxs(wallHeight, zoomedPxInInches);
  const convertedGuideWidth = inchesToPxs(columns, zoomedPxInInches);
  const convertedGuideHeight = inchesToPxs(rows, zoomedPxInInches);
  const wallContainerPadding = !enableTouchPointsOnBothSides
    ? 16
    : inchesToPxs(DEFAULT_TOUCH_POINT_START_COLUMN, zoomedPxInInches);

  const baseBoardHeight = 3; // Standard base board height is 3 to 5 inches
  const convertedBaseBoardHeight = inchesToPxs(baseBoardHeight, zoomedPxInInches);

  const [currentFloorHeight, setCurrentFloorHeight] = useState(0);
  const floorHeightInInches = (isMobile ? 24 : 12) / zoom; // Default floor height in inches
  const floorHeight = isMobile ? 60 : inchesToPxs(floorHeightInInches, zoomedPxInInches);

  const {
    cabinetVisualizer,
  } = useCabinetVisualizerStore();

  const anchorSide = selectDesignAnchorSide(cabinetVisualizer, designId);
  const { combinedTopProducts, combinedBottomProducts } = selectTopAndBottomProducts(cabinetVisualizer, designId);

  const touchPoints = useTouchPointManager(
    cabinets, combinedTopProducts, combinedBottomProducts,
    { anchorSide, disableTouchPoints }, enableTouchPointsOnBothSides);

  // Calculate total bounding from cabinets
  const cabinetBoundings = cabinets?.map((cabinet) => cabinetToBoundingBox(cabinet)) || [];
  const totalBounding = getTotalBounding(cabinetBoundings);

  const renderWall = cabinets.length !== 0 && rows && columns;
  const newConvertedBounding = boundingToScreen(totalBounding, zoomedPxInInches);
  const convertedFloorWidth = convertedWidth > newConvertedBounding.right ? convertedWidth : newConvertedBounding.right;

  const getArrowClassNameText = (index) => {
    switch (index) {
    case 0:
      return 'arrow arrow-top arrow-left';
    case 1:
      return 'arrow arrow-top arrow-right';
    case 2:
      return 'arrow arrow-bottom arrow-left';
    default:
      return 'arrow arrow-bottom arrow-right';
    }
  };
  const getArrowAltText = (index) => {
    switch (index) {
    case 0:
      return 'arrow pointing to the top left interaction point';
    case 1:
      return 'arrow pointing to the top right interaction point';
    case 2:
      return 'arrow pointing to the bottom left interaction point';
    default:
      return 'arrow pointing to the bottom right interaction point';
    }
  };
  const arrowImage = (index, convertedPoint) => (
    <img
      key={index}
      className={getArrowClassNameText(index)}
      src="https://blinds.homedepot.com/product-images/7d36234f-e2d9-ef11-848c-0afffee37a07.png"
      width="140px"
      height="80px"
      alt={getArrowAltText(index)}
    />
  );
  const offsetTouchPoint = inchesToPxs(DEFAULT_TOUCH_POINT_WIDTH, zoomedPxInInches);

  const floorBackgroundStyles = {
    floorBackground: {
      width: '100%',
      // bottom: -(floorHeight),
      height: floorHeight
    },
    baseBoardBackground: {
      width: '100%',
      bottom: floorHeight,
      height: convertedBaseBoardHeight,
    }
  };

  const finalSegments = segments.length === 0 ? getSegments(columns, getConstrainedWallHeight(rows)) : segments;
  const getJustifyContent = () => {
    if (cabinets.length === 0) {
      return 'center';
    }

    if (anchorSide === 'left') {
      return 'flex-start';
    }
    return 'flex-end';
  };

  const defaultWallWidth = DEFAULT_VIEWPORT_WALLSIZE.Columns * zoomedPxInInches;
  const defaultWallHeight = DEFAULT_VIEWPORT_WALLSIZE.Rows * zoomedPxInInches;

  const getConstrainedHeight = () => {
    if (cabinets.length === 0) {
      return defaultWallHeight;
    }

    if (!convertedGuideHeight || convertedGuideHeight === 0) {
      return convertedHeight;
    }
    return convertedGuideHeight;
  };

  const getConstrainedWallWidth = () => {
    if (!hasWallMeasurements) {
      return defaultWallWidth;
    }
    if (!convertedGuideWidth || convertedGuideWidth === 0) {
      return convertedWidth;
    }
    return convertedGuideWidth;
  };

  const setSelectedCabinet = useCabinetStore((state) => state.setSelectedCabinet);
  const handleWallClick = (event) => {
    // Prevent clearing when clicking on a cabinet or popover
    if (
      event.target.closest('.cabinet')
      || event.target.closest('.cabinet-image-popover')
    ) {
      return;
    }
    setSelectedCabinet(null);
  };

  useEffect(() => {
    if (onFloorRectChange && floorRef.current) {
      if (currentFloorHeight !== floorHeight) {
        const rect = floorRef.current.getBoundingClientRect();
        onFloorRectChange(rect);
        setCurrentFloorHeight(floorHeight);

      }
    }
  }, [floorHeight]);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className="wall-container"
      style={{
        width: '100%',
        marginTop: '60px',
        height: convertedHeight + floorHeight,
        transform: `translate(${scrollOffset.scrollLeft}px, ${scrollOffset.scrollTop}px)`, // Adjust for scroll
      }}
      ref={combinedRef} // Attach droppable ref
      onClick={handleWallClick} // Handle wall click to clear selection
    >
      <div
        className="cabinets-overflow-sizer"
        style={{
          width: 0,
          height: 0,
          paddingLeft: newConvertedBounding.left < 0 ? -newConvertedBounding.left : 0,
          position: 'relative'
        }}
      />
      <div
        className="wall-baseboard-background"
        style={floorBackgroundStyles.baseBoardBackground}
      />
      <div
        ref={floorRef}
        className="wall-floor-background"
        style={floorBackgroundStyles.floorBackground}
      />
      <div
        className="wall-content"
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: getJustifyContent(),
          ...(cabinets.length > 0 && anchorSide === 'left' && { paddingLeft: `${wallContainerPadding}px` }),
          ...(cabinets.length > 0 && anchorSide === 'right' && { paddingRight: `${wallContainerPadding}px` }),

        }}
      >
        {showDimensions && (
          <DimensionArrowsContainer
            designId={designId}
            rows={wallHeight}
            columns={wallWidth}
            zoom={zoom}
            pxInInches={pxInInches}
            anchorSide={anchorSide}
            floorOffset={floorHeightInInches}
          />
        )}
        <div
          className="wall"
          style={{
            background: 'none',
            position: 'absolute',
            zIndex: 3,
            bottom: floorHeight,
            width: defaultWallWidth,
            height: defaultWallHeight,
          }}
        >
          {!showDimensions && cabinets.length === 0 && (
            <div className="emptyDesignInstructions">
              <span className="instructionLineOne">
                Add cabinets to preview them on your wall
              </span>
              <span className="instructionLineTwo">
                Choose left or right side to start your cabinet project
              </span>
            </div>
          )}
          {showDimensions && cabinets?.map((cab) => {
            const { Row, Column, Width, Height } = originToScreen(cab.Origin, zoomedPxInInches);
            return (
              <CabinetDimensions
                key={`${cab.Sku}-${cab.Origin.Column}--${cab.Origin.Row}`}
                top={Row}
                left={Column}
                width={Width}
                height={Height}
                heightDim={cab.Origin.Height}
                widthDim={cab.Origin.Width}
              />
            );
          })}
          {!showDimensions && dndEnabled && cabinets?.filter((cab) => !(activeCabinet && cab.id === activeCabinet.id))
            .map((cab) => {
              const { Row, Column, Width, Height } = originToScreen(cab.Origin, zoomedPxInInches);
              return (
                <DraggableCabinet
                  cabinetId={cab.id}
                  top={Row}
                  left={Column}
                  width={Width}
                  height={Height}
                  itemId={cab.Sku}
                  mirror={cab.mirror}
                  handleQuickView={handleQuickView}
                  handleDuplicate={handleDuplicate}
                  designId={designId}
                  origin={cab.Origin}
                  spacer={cab.Sku > 0}
                  spacerName={cab.Name}
                  cabinets={cabinets}
                  topCabinets={combinedTopProducts}
                  bottomCabinets={combinedBottomProducts}
                  anchorSide={anchorSide}
                  border={selectedCabinet?.id === cab.id && !activeCabinet}
                />
              );
            })}
          {!showDimensions && !dndEnabled && cabinets?.map((cab) => {
            const { Row, Column, Width, Height } = originToScreen(cab.Origin, zoomedPxInInches);
            return (
              <Cabinet
                id={cab.id}
                top={Row}
                left={Column}
                width={Width}
                height={Height}
                itemId={cab.Sku}
                mirror={cab.mirror}
                handleQuickView={handleQuickView}
                handleDuplicate={handleDuplicate}
                designId={designId}
                origin={cab.Origin}
                spacer={cab.Sku > 0}
                spacerName={cab.Name}
                cabinets={cabinets}
                topCabinets={combinedTopProducts}
                bottomCabinets={combinedBottomProducts}
                anchorSide={anchorSide}
                border={selectedCabinet?.id === cab.id}
              />
            );
          })}
          {!showDimensions && (
            <DragOverlay
              adjustScale={true}
              dropAnimation={null}
              container={wallContainerRef.current}
            >
              {activeCabinet && overlayPosition ? (
                <DragOverlayCabinet
                  id={activeCabinet?.id}
                  left={overlayPosition?.Column || 0}
                  top={overlayPosition?.Row || 0}
                  width={overlayPosition?.Width || 0}
                  height={overlayPosition?.Height || 0}
                  mirror={activeCabinet?.mirror}
                  origin={activeCabinet?.origin}
                  itemId={activeCabinet?.Sku}
                  spacerName={activeCabinet?.Name}
                  voidSpacer={overlayPosition?.voidSpacer}
                />
              ) : null}
            </DragOverlay>
          )}
          {!showDimensions && touchPoints.map((curPoint, index) => {
            const convertedPoint = touchPointToScreen(curPoint, zoomedPxInInches);
            return (
              <TouchPoint
                key={`touchpoint-${index}`}
                top={convertedPoint.top}
                zoom={zoom}
                left={convertedPoint.left}
                width={convertedPoint.width}
                height={convertedPoint.height}
                onTouchPoint={(event) => onTouchPoint(event, curPoint)}
                onTouchPointForSpacer={(event) => onTouchPointForSpacer(event, curPoint)}
                onTouchPointForFiller={(event) => onTouchPointForFiller(event, curPoint)}
                showText={cabinets.length === 0}
                type={convertedPoint.type[0]}
              />
            );
          })}
        </div>
        {(finalSegments)?.map(({ Start, End, Type }) => (
          <Segment
            left={Start.Column}
            top={Start.Row}
            width={End.Column - Start.Column}
            height={End.Row - Start.Row}
            pxInInches={zoomedPxInInches}
            type={Type}
          />
        ))}
        {hasWallMeasurements && cabinets.length > 0 && (
          <div
            className="wall-guide"
            style={{
              background: 'rgba(255,255,255,.2)',
              position: 'absolute',
              zIndex: 2,
              width: getConstrainedWallWidth(),
              height: getConstrainedHeight(),
              bottom: floorHeight,
              ...(anchorSide === DIRECTION.LEFT && { left: wallContainerPadding + offsetTouchPoint }),
              ...(anchorSide === DIRECTION.RIGHT && { right: wallContainerPadding + offsetTouchPoint }),
            }}
          />
        )}
      </div>
    </div>
  );
});

Wall.propTypes = {
  cabinets: arrayOf(CabinetShape).isRequired,
  segments: arrayOf(SegmentShape),
  rows: number.isRequired,
  columns: number.isRequired,
  pxInInches: number.isRequired,
  zoom: number,
  onTouchPoint: func,
  onTouchPointForSpacer: func,
  onTouchPointForFiller: func,
  handleQuickView: func.isRequired,
  handleDuplicate: func.isRequired,
  designId: string.isRequired,
  showDimensions: bool,
  scrollOffset: shape({
    scrollLeft: number,
    scrollTop: number,
  }),
  onFloorRectChange: func,
  onWallContainerRefChange: func,
  disableTouchPoints: bool,
  overlayPosition: shape({
    Column: number,
    Row: number,
    Width: number,
    Height: number
  })
};

Wall.defaultProps = {
  zoom: 1,
  onTouchPoint: () => { },
  onTouchPointForSpacer: () => { },
  onTouchPointForFiller: () => { },
  segments: [],
  showDimensions: false,
  scrollOffset: { scrollLeft: 0, scrollTop: 0 },
  onFloorRectChange: () => { },
  onWallContainerRefChange: () => { },
  disableTouchPoints: false,
  overlayPosition: {
    Column: 0,
    Row: 0,
    Width: 0,
    Height: 0
  }
};
